import { useQuery, useMutation, keepPreviousData, useQueryClient } from '@tanstack/react-query';

import api from '../../lib/api';

import {
  CustomerDetailData,
  ApproveCustomerVars,
  UpdateCustomerVars,
  SetDocStatusVars,
  SetDocStatusResponse,
  SetDocStatusContext,
  UploadDocPayload,
  DownloadDocVars,
  CustomerProductsCatalogParams,
  CustomerId,
  CustomerProductsCatalogResponse,
  CustomerProductAccessPayload,
  CreateCustomerPayload,
  InviteCustomerPayload,
  InviteManualCustomerPayload,
  ListCustomersParams,
} from './types';

export * from './types';

export type CustomerSubuserStatus = 'INVITED' | 'ACTIVE' | 'DISABLED';

export type CustomerSubuserRole = 'PRIMARY' | 'SUBUSER';

export interface AdminCustomerSubuserRow {
  id: string;
  customerId: string;
  userId: string | null;
  email: string;
  name: string | null;
  role: CustomerSubuserRole;
  status: CustomerSubuserStatus;
  inviteExpiresAt: string | null;
  createdAt: string;
  updatedAt: string;

  user?: {
    firebaseUid: string;
    email: string;
    name: string | null;
    phone?: string | null;
  } | null;

  enabledAddressCount?: number;
}

export interface AdminCustomerSubuserListResponse {
  subusers: AdminCustomerSubuserRow[];
  totalCount?: number;
  page?: number;
  pageSize?: number;
  totalPages?: number;
}

export interface InviteAdminCustomerSubuserPayload {
  email: string;
  name: string;
}

export interface InviteAdminCustomerSubuserResponse {
  customerUserId: string;
  inviteExpiresAt: string;
  inviteUrl?: string;
}

export interface UpdateAdminCustomerSubuserPayload {
  status: 'ACTIVE' | 'DISABLED';
}

export interface CustomerSubuserAddressAccessItem {
  id: string;
  label: string | null;
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  state: string | null;
  postalCode: string | null;
  isEnabled: boolean;
}

export interface AdminCustomerSubuserAddressAccessResponse {
  subuser: {
    id: string;
    email: string;
    name: string | null;
    status: CustomerSubuserStatus;
  };

  addresses: CustomerSubuserAddressAccessItem[];
}

const qk = {
  customerAddresses: (customerId: string) => ['customer-addresses', customerId] as const,
};

export const useListCustomers = (params: ListCustomersParams) =>
  useQuery({
    queryKey: ['customers', params],

    queryFn: async () =>
      (
        await api.get('/organization/customers', {
          params,
        })
      ).data,

    placeholderData: keepPreviousData,
  });

export const useInviteCustomer = () => {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (payload: InviteCustomerPayload) =>
      (await api.post('/organization/customers/invite', payload)).data,

    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: ['customers'],
      });

      void qc.invalidateQueries({
        queryKey: ['auditLogs'],
      });
    },
  });
};

export const useInviteManualCustomer = (id: string) => {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (payload: InviteManualCustomerPayload) =>
      (await api.post(`/organization/customers/${id}/invite`, payload)).data,

    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: ['customers'],
      });

      void qc.invalidateQueries({
        queryKey: ['auditLogs'],
      });
    },
  });
};

export function useInviteInfo(token: string, isEnabled: boolean) {
  return useQuery({
    queryKey: ['invite-info', token],

    queryFn: async () => (await api.get(`/organization/customers/invites/${token}`)).data,

    enabled: !!token && isEnabled,
  });
}

export const useUploadInviteDoc = (token: string) =>
  useMutation({
    mutationFn: async ({ type, file }: UploadDocPayload) => {
      const fd = new FormData();

      fd.append('type', type);
      fd.append('file', file);

      const resp = await api.post(`/organization/customers/invites/${token}/documents`, fd, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      return resp.data;
    },
  });

export function useUploadDocForCustomer(customerId: string) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async ({ type, file }: UploadDocPayload) => {
      const fd = new FormData();

      fd.append('type', type);
      fd.append('file', file);

      const resp = await api.post(`/organization/customers/${customerId}/documents`, fd, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      return resp.data;
    },

    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: customersKeys.detail(customerId),
      });

      void qc.invalidateQueries({
        queryKey: ['customers'],
      });

      void qc.invalidateQueries({
        queryKey: ['auditLogs'],
      });
    },
  });
}

export function useUpdateCustomerRequirements() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (payload: {
      customerId: string;
      isAgreementRequered?: boolean;
      isResaleCertificateRequered?: boolean;
    }) => {
      const { customerId, ...body } = payload;

      const res = await api.patch(`/organization/customers/${customerId}/requirements`, body);

      return res.data;
    },

    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({
        queryKey: customersKeys.detail(vars.customerId),
      });

      void qc.invalidateQueries({
        queryKey: ['customers'],
      });

      void qc.invalidateQueries({
        queryKey: ['auditLogs'],
      });
    },
  });
}

export const useAcceptInvite = (token: string) =>
  useMutation({
    mutationFn: async () =>
      (await api.post(`/organization/customers/invites/${token}/accept`)).data,
  });

export const customersKeys = {
  list: (params: ListCustomersParams) => ['customers', 'list', params] as const,

  detail: (id: string) => ['customers', 'detail', id] as const,
};

export function useGetCustomer(id?: string | null) {
  return useQuery({
    queryKey: customersKeys.detail(id || 'none'),

    queryFn: async () => {
      const { data } = await api.get(`/organization/customers/${id}`);

      return data;
    },

    enabled: !!id,
    staleTime: 30_000,
  });
}

export function useApproveCustomer() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (vars: ApproveCustomerVars) => {
      const { data } = await api.post(`/organization/customers/${vars.customerId}/approve`, {
        locationId: vars.locationId ?? null,

        name: vars.name,

        defaultFulfillmentMethodId: vars.defaultFulfillmentMethodId ?? null,

        ...(vars.tierId !== undefined
          ? {
              tierId: vars.tierId,
            }
          : {}),

        ...(vars.applyTierToAllLocations !== undefined
          ? {
              applyTierToAllLocations: vars.applyTierToAllLocations,
            }
          : {}),
      });

      return data;
    },

    onSuccess: (_data, vars) => {
      const { customerId } = vars;

      void qc.invalidateQueries({
        queryKey: customersKeys.detail(customerId),
      });

      void qc.invalidateQueries({
        queryKey: ['customers'],
      });

      // Region / tier / pricing-visibility changes alter the pricing estimate on
      // this customer's loads, so refresh the (frozen) order-item queries too.
      void qc.invalidateQueries({ queryKey: ['org:orders:items'] });
      void qc.invalidateQueries({ queryKey: ['org:orders:item'] });

      if (vars.tierId !== undefined || vars.applyTierToAllLocations !== undefined) {
        void qc.invalidateQueries({
          queryKey: ['customer-tiers'],
        });

        void qc.invalidateQueries({
          queryKey: qk.customerAddresses(customerId),
        });

        void qc.invalidateQueries({
          queryKey: ['addresses', customerId],
        });
      }

      void qc.invalidateQueries({
        queryKey: ['auditLogs'],
      });
    },
  });
}

function pickFilenameFromDisposition(disposition?: string, fallback = 'document.pdf') {
  if (!disposition) {
    return fallback;
  }

  const star = /filename\*=UTF-8''([^;]+)/i.exec(disposition);

  if (star?.[1]) {
    return decodeURIComponent(star[1]);
  }

  const quoted = /filename="([^"]+)"/i.exec(disposition);

  if (quoted?.[1]) {
    return quoted[1];
  }

  return fallback;
}

export function useDownloadCustomerDoc() {
  return useMutation({
    mutationFn: async ({ docId, name }: DownloadDocVars) => {
      const resp = await api.get(`/organization/customers/docs/${docId}/download`, {
        responseType: 'blob',
      });

      const filename = pickFilenameFromDisposition(
        (resp as any)?.headers?.['content-disposition'],
        name || 'document.pdf'
      );

      const url = URL.createObjectURL(new Blob([resp.data]));

      const a = document.createElement('a');

      a.href = url;
      a.download = filename;

      document.body.appendChild(a);

      a.click();
      a.remove();

      URL.revokeObjectURL(url);

      return true;
    },
  });
}

/**
 * Fetches a customer document as an object URL for
 * the preview dialog.
 *
 * The blob is re-typed as PDF so the iframe renders
 * it rather than downloading it.
 */

export function usePreviewCustomerDoc() {
  return useMutation<Blob, Error, { docId: string }>({
    mutationFn: async ({ docId }) => {
      const resp = await api.get<Blob>(
        `/organization/customers/docs/${encodeURIComponent(docId)}/download`,
        { responseType: 'blob' }
      );

      return resp.data;
    },
  });
}

export const selfCustomerKeys = {
  root: ['me', 'customer'] as const,

  detail: () => [...selfCustomerKeys.root, 'detail'] as const,
};

export function useGetMyCustomer() {
  return useQuery({
    queryKey: selfCustomerKeys.detail(),

    queryFn: async () => {
      const { data } = await api.get('/customer');

      return data as CustomerDetailData;
    },

    staleTime: 30_000,
  });
}

export function useUploadMyCustomerDoc() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (payload: UploadDocPayload) => {
      const fd = new FormData();

      fd.set('type', payload.type);
      fd.set('file', payload.file);

      const { data } = await api.post('/customer/documents', fd, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      return data;
    },

    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: selfCustomerKeys.root,
      });
    },
  });
}

export function useDownloadSelfCustomerDoc() {
  return useMutation({
    mutationFn: async ({ docId, name }: DownloadDocVars) => {
      const resp = await api.get(`/customer/documents/${docId}/download`, {
        responseType: 'blob',
      });

      const filename = pickFilenameFromDisposition(
        (resp as any)?.headers?.['content-disposition'],
        name || 'document.pdf'
      );

      const url = URL.createObjectURL(new Blob([resp.data]));

      const a = document.createElement('a');

      a.href = url;
      a.download = filename;

      document.body.appendChild(a);

      a.click();
      a.remove();

      URL.revokeObjectURL(url);

      return true;
    },
  });
}

export function usePreviewSelfCustomerDoc() {
  return useMutation<Blob, Error, { docId: string }>({
    mutationFn: async ({ docId }) => {
      const response = await api.get<Blob>(
        `/customer/documents/${encodeURIComponent(docId)}/download`,
        { responseType: 'blob' }
      );

      // The authenticated download route already returns the file data.
      // Give object URLs a PDF MIME type for reliable iframe rendering.
      return new Blob([response.data], { type: 'application/pdf' });
    },
  });
}

export function useSetCustomerDocStatus(customerId: string) {
  const qc = useQueryClient();

  return useMutation<SetDocStatusResponse, unknown, SetDocStatusVars, SetDocStatusContext>({
    mutationFn: async ({ docId, status, notes }) => {
      const { data } = await api.patch(`/organization/customers/documents/${docId}/status`, {
        status,
        notes: notes ?? null,
      });

      return data as SetDocStatusResponse;
    },

    onMutate: async (vars) => {
      await qc.cancelQueries({
        queryKey: customersKeys.detail(customerId),
      });

      const previous = qc.getQueryData<CustomerDetailData>(customersKeys.detail(customerId));

      qc.setQueryData<CustomerDetailData>(customersKeys.detail(customerId), (old) => {
        if (!old?.customer) {
          return old;
        }

        const idx = old.customer.documents.findIndex((document) => document.id === vars.docId);

        if (idx === -1) {
          return old;
        }

        const target = old.customer.documents[idx];

        const documents = old.customer.documents.map((document, index) =>
          index === idx
            ? {
                ...document,
                status: vars.status,
                notes: vars.notes ?? null,
              }
            : document
        );

        let missing = old.missing ?? [];

        const type = target.type;

        const isRequired =
          (type === 'AGREEMENT' && old.customer.isAgreementRequered) ||
          (type === 'RESALE_CERTIFICATE' && old.customer.isResaleCertificateRequered);

        if (isRequired && vars.status === 'REJECTED' && !missing.includes(type)) {
          missing = [...missing, type];
        }

        return {
          ...old,

          customer: {
            ...old.customer,
            documents,
          },

          missing,
        };
      });

      return {
        previous,
      };
    },

    onError: (_error, _vars, context) => {
      if (context?.previous) {
        qc.setQueryData<CustomerDetailData>(customersKeys.detail(customerId), context.previous);
      }
    },

    onSettled: () => {
      void qc.invalidateQueries({
        queryKey: customersKeys.detail(customerId),
      });

      void qc.invalidateQueries({
        queryKey: ['customers'],
      });

      void qc.invalidateQueries({
        queryKey: ['auditLogs'],
      });
    },
  });
}

export function useUpdateCustomer() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (vars: UpdateCustomerVars) => {
      const { data } = await api.patch(`/organization/customers/${vars.customerId}`, {
        locationId: vars.locationId,

        name: vars.name,

        notificationEmail: vars.notificationEmail,

        ...(vars.tierId !== undefined
          ? {
              tierId: vars.tierId,
            }
          : {}),

        ...(vars.applyTierToAllLocations !== undefined
          ? {
              applyTierToAllLocations: vars.applyTierToAllLocations,
            }
          : {}),

        ...(vars.region !== undefined ? { region: vars.region } : {}),

        ...(vars.showPricingToCustomer !== undefined
          ? { showPricingToCustomer: vars.showPricingToCustomer }
          : {}),
      });

      return data;
    },

    onSuccess: (_data, vars) => {
      const { customerId } = vars;

      void qc.invalidateQueries({
        queryKey: customersKeys.detail(customerId),
      });

      void qc.invalidateQueries({
        queryKey: ['customers'],
      });

      // Region / tier / pricing-visibility changes alter the pricing estimate on
      // this customer's loads, so refresh the (frozen) order-item queries too.
      void qc.invalidateQueries({ queryKey: ['org:orders:items'] });
      void qc.invalidateQueries({ queryKey: ['org:orders:item'] });

      if (vars.tierId !== undefined || vars.applyTierToAllLocations !== undefined) {
        void qc.invalidateQueries({
          queryKey: ['customer-tiers'],
        });

        void qc.invalidateQueries({
          queryKey: qk.customerAddresses(customerId),
        });

        void qc.invalidateQueries({
          queryKey: ['addresses', customerId],
        });
      }

      void qc.invalidateQueries({
        queryKey: ['auditLogs'],
      });
    },
  });
}

export interface SendOrderDetailsResult {
  sent: boolean;
  openOrderCount: number;
}

// One-off: email the customer a summary of their open orders now.
export function useSendCustomerOrderDetails() {
  return useMutation({
    mutationFn: async (customerId: string) => {
      const { data } = await api.post<SendOrderDetailsResult>(
        `/organization/customers/${customerId}/send-order-details`,
        {}
      );
      return data;
    },
  });
}

// Plain (non-hook) send for fan-out callers like the bulk Send order list action.
export async function sendOrderDetailsDirect(customerId: string): Promise<SendOrderDetailsResult> {
  const { data } = await api.post<SendOrderDetailsResult>(
    `/organization/customers/${customerId}/send-order-details`,
    {}
  );
  return data;
}

export function useArchiveCustomer() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (customerId: string) => {
      const { data } = await api.post(`/organization/customers/${customerId}/archive`, {});

      return data;
    },

    onSuccess: (_data, customerId) => {
      void qc.invalidateQueries({
        queryKey: customersKeys.detail(customerId),
      });

      void qc.invalidateQueries({
        queryKey: ['customers'],
      });

      void qc.invalidateQueries({
        queryKey: ['auditLogs'],
      });
    },
  });
}

export function useUnarchiveCustomer() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (customerId: string) => {
      const { data } = await api.post(`/organization/customers/${customerId}/unarchive`, {});

      return data;
    },

    onSuccess: (_data, customerId) => {
      void qc.invalidateQueries({
        queryKey: customersKeys.detail(customerId),
      });

      void qc.invalidateQueries({
        queryKey: ['customers'],
      });

      void qc.invalidateQueries({
        queryKey: ['auditLogs'],
      });
    },
  });
}

export function useDownloadOrgAgreementForCustomer() {
  return useMutation({
    mutationFn: async () => {
      const resp = await api.get('/organization/agreement/customer/download', {
        responseType: 'blob',
      });

      return resp.data as Blob;
    },
  });
}

export function useCustomerProductsCatalog(
  customerId: CustomerId,
  params: CustomerProductsCatalogParams
) {
  return useQuery<CustomerProductsCatalogResponse>({
    queryKey: ['customer-products-catalog', customerId, params],

    enabled: Boolean(customerId),

    queryFn: async () => {
      const { data } = await api.get<CustomerProductsCatalogResponse>(
        `/organization/customers/${customerId}/products/catalog`,
        {
          params,
        }
      );

      return data;
    },

    placeholderData: (previous) => previous,
  });
}

export function useCustomerCatalogWithAccess(customerId?: string | null, search?: string) {
  const trimmed = (search ?? '').trim();

  return useQuery({
    queryKey: [
      'customer-products-catalog',
      customerId,
      {
        scope: 'access',
        search: trimmed,
      },
    ],

    enabled: Boolean(customerId),
    staleTime: 30_000,

    queryFn: async () => {
      const { data } = await api.get<CustomerProductsCatalogResponse>(
        `/organization/customers/${customerId}/products/catalog`,
        {
          params: {
            page: 1,
            pageSize: 500,

            ...(trimmed
              ? {
                  search: trimmed,
                }
              : {}),
          },
        }
      );

      const enabledSet = new Set(data.enabledProductIds);

      return {
        ...data,

        products: data.products.map((product) => ({
          ...product,

          isEnabledForCustomer: enabledSet.has(product.id),
        })),
      };
    },
  });
}

export function useSetCustomerProductAccess(customerId: CustomerId) {
  const qc = useQueryClient();

  return useMutation<unknown, unknown, CustomerProductAccessPayload>({
    mutationFn: async ({ productId, isEnabled, isPreview, deliveredPriceCents }) => {
      const { data } = await api.put(
        `/organization/customers/${customerId}/products/${productId}`,
        {
          ...(isEnabled !== undefined
            ? {
                isEnabled,
              }
            : {}),

          ...(isPreview !== undefined
            ? {
                isPreview,
              }
            : {}),

          ...(deliveredPriceCents !== undefined ? { deliveredPriceCents } : {}),
        }
      );

      return data;
    },

    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: ['customer-products-catalog', customerId],
      });

      void qc.invalidateQueries({
        queryKey: ['customer-product-pricing', customerId],
      });

      void qc.invalidateQueries({
        queryKey: ['auditLogs'],
      });
    },
  });
}

export type CustomerProductPricingRow = {
  id: string;
  name: string;
  deliveryType?: string | null;
  deliveredPriceCents: number | null;
  isEnabled: boolean;
};

/** Every org product + this customer's Northeast delivered price (cents). */
export function useCustomerProductPricing(customerId: string | null, enabled = true) {
  return useQuery({
    queryKey: ['customer-product-pricing', customerId],

    queryFn: async () => {
      const { data } = await api.get(`/organization/customers/${customerId}/product-pricing`);

      return data as { products: CustomerProductPricingRow[] };
    },

    enabled: enabled && !!customerId,
    staleTime: 30_000,
  });
}

export function useListAddresses(customerId: string | null) {
  return useQuery({
    queryKey: qk.customerAddresses(customerId || ''),

    queryFn: async () => {
      const { data } = await api.get(`/organization/customers/${customerId}/addresses`);

      return data;
    },

    enabled: !!customerId,
    staleTime: 30_000,
  });
}

export function useDownloadInviteAgreement(token?: string) {
  return useMutation({
    mutationFn: async () => {
      if (!token) {
        throw new Error('No invite token');
      }

      const resp = await api.get(`/organization/customers/invites/${token}/agreement/download`, {
        responseType: 'blob',
      });

      return resp.data as Blob;
    },
  });
}

export function useUpdateCustomerPrepay() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (payload: { customerId: string; requiresPrepay: boolean }) => {
      const { customerId, ...body } = payload;

      const res = await api.patch(`/organization/customers/${customerId}/prepay`, body);

      return res.data;
    },

    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({
        queryKey: customersKeys.detail(vars.customerId),
      });

      void qc.invalidateQueries({
        queryKey: ['customers'],
      });

      void qc.invalidateQueries({
        queryKey: ['auditLogs'],
      });
    },
  });
}

export function useListCustomerLoadOptions(customerId: string | null) {
  return useQuery({
    queryKey: ['customer-load-options', customerId],

    queryFn: async () => {
      const { data } = await api.get(`/organization/customers/${customerId}/load-options`);

      return data;
    },

    enabled: !!customerId,
    staleTime: 30_000,
  });
}

export const useCreateCustomer = () => {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (payload: CreateCustomerPayload) =>
      (await api.post('/organization/customers/create', payload)).data,

    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: ['customers'],
      });

      void qc.invalidateQueries({
        queryKey: ['auditLogs'],
      });
    },
  });
};

export interface CustomerAddressWarehouse {
  id: string;
  name: string;
}

export interface CustomerAddressCarrierRate {
  id?: string;
  warehouseId: string;
  carrierRateCents: number;
  warehouse?: CustomerAddressWarehouse;
}

export interface CustomerAddressProductAssignment {
  productId: string;
  isEnabled: boolean;
  isPreview: boolean;
  deliveredPriceCents?: number | null;
}

export interface CustomerAddressProduct {
  id: string;
  name: string;
  imageUrl?: string | null;
  deliveryType?: string | null;
  warehouseIds: string[];
}

export interface CustomerAddressLoadOption {
  assignmentId: string;
  orgLoadOptionId: string;
  active: boolean;

  id: string;
  name: string;

  capacityLabel?: string | null;
  enableCarrierRate?: boolean;
}

export interface CustomerAddressAvailableLoadOption {
  id: string;
  name: string;

  capacityLabel?: string | null;
  enableCarrierRate?: boolean;

  /** The org-wide default method, inherited by locations with no methods of their own. */
  isDefault?: boolean;
}

export interface CustomerAddressTier {
  id: string;
  name: string;
  discountAmountCents: number;
  badgeColor: string;
}

export interface CustomerAddress {
  id: string;
  customerId: string;
  userId?: string | null;

  label?: string | null;

  fullName: string;
  phone: string;

  line1: string;
  line2?: string | null;

  city: string;
  state: string;
  postalCode: string;
  country: string;

  isPrimary: boolean;
  isHeadquarters: boolean;

  /** Pricing region for this delivery location (drives the price structure). */
  region?: 'NORTHEAST' | 'SOUTHERN' | 'OTHER' | null;

  /**
   * When true, new customer orders to this delivery
   * location are blocked until an org admin unfreezes it.
   */
  onFreeze: boolean;

  /**
   * Whether this location participates in the customer's
   * currently assigned tier discount.
   */
  applyCustomerTierDiscount: boolean;

  products: CustomerAddressProductAssignment[];

  configuredProductCount?: number;

  warehouses?: CustomerAddressWarehouse[];

  carrierRates: CustomerAddressCarrierRate[];

  configuredCarrierRateCount: number;
  availableWarehouseCount: number;

  defaultFulfillmentMethodId?: string | null;

  /** Whether orders to this sub-location require a PO number. */
  requiresPoNumber?: boolean;

  loadOptions?: CustomerAddressLoadOption[];

  configuredLoadOptionCount?: number;
  availableLoadOptionCount?: number;

  customerUsers?: Array<{
    id: string;
    name?: string | null;
    email: string;
    status?: string;
    isActive?: boolean;
  }>;

  createdAt: string;
  updatedAt: string;
}

export interface CustomerAddressPayload {
  label?: string | null;

  fullName: string;
  phone: string;

  line1: string;
  line2?: string | null;

  city: string;
  state: string;
  postalCode: string;
  country: string;

  isPrimary?: boolean;
  isHeadquarters?: boolean;

  requiresPoNumber?: boolean;
  operatingHours?: unknown;

  salesRepUserId?: string | null;

  /** Pricing region for this delivery location. */
  region?: 'NORTHEAST' | 'SOUTHERN' | 'OTHER' | null;

  /**
   * Only meaningful when the customer has a tier assigned.
   */
  applyCustomerTierDiscount?: boolean;

  products?: CustomerAddressProductAssignment[];

  carrierRates: Array<{
    warehouseId: string;
    carrierRateCents: number;
  }>;

  orgLoadOptionIds: string[];

  defaultFulfillmentMethodId: string;
}

export interface CustomerAddressesResponse {
  addresses: CustomerAddress[];

  /**
   * The customer owns the tier. Locations only store the
   * applyCustomerTierDiscount flag.
   */
  customerTier: CustomerAddressTier | null;

  warehouses: CustomerAddressWarehouse[];

  products: CustomerAddressProduct[];

  perItemPricing: boolean;

  /** Master pricing switch — gates the per-location region + delivered-price UI. */
  pricingEnabled?: boolean;

  /** Customer-level visibility toggle, editable from this modal. */
  showPricingToCustomer?: boolean;

  loadOptions?: CustomerAddressAvailableLoadOption[];
}

export function useListCustomerAddresses(customerId?: string | null) {
  return useQuery({
    queryKey: qk.customerAddresses(customerId || ''),

    enabled: !!customerId,

    queryFn: async () => {
      const resp = await api.get<CustomerAddressesResponse>(
        `/organization/customers/${customerId}/addresses`
      );

      return resp.data;
    },
  });
}

/** Toggle the customer-level "show pricing to customer" flag from the addresses
 * modal. Persists on the customer; refreshes the addresses view + any loads. */
export function useSetCustomerShowPricing(customerId: string) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (showPricingToCustomer: boolean) => {
      const { data } = await api.patch(`/organization/customers/${customerId}`, {
        showPricingToCustomer,
      });
      return data;
    },

    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.customerAddresses(customerId) });
      void qc.invalidateQueries({ queryKey: customersKeys.detail(customerId) });
      void qc.invalidateQueries({ queryKey: ['customers'] });
      void qc.invalidateQueries({ queryKey: ['org:orders:items'] });
      void qc.invalidateQueries({ queryKey: ['org:orders:item'] });
    },
  });
}

export function useCreateCustomerAddress(customerId: string) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (payload: CustomerAddressPayload) => {
      const resp = await api.post<{
        address: CustomerAddress;
      }>(`/organization/customers/${customerId}/addresses`, payload);

      return resp.data;
    },

    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: qk.customerAddresses(customerId),
      });

      void qc.invalidateQueries({
        queryKey: ['auditLogs'],
      });
    },
  });
}

export function useUpdateCustomerAddress(customerId: string) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async ({
      addressId,
      data,
    }: {
      addressId: string;
      data: Partial<CustomerAddressPayload>;
    }) => {
      const resp = await api.patch<{
        address: CustomerAddress;
      }>(`/organization/customers/${customerId}/addresses/${addressId}`, data);

      return resp.data;
    },

    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: qk.customerAddresses(customerId),
      });

      // Region / delivered prices live on the address now, so a save changes the
      // pricing estimate on this location's loads — refresh the order-item queries.
      void qc.invalidateQueries({ queryKey: ['org:orders:items'] });
      void qc.invalidateQueries({ queryKey: ['org:orders:item'] });

      void qc.invalidateQueries({
        queryKey: ['auditLogs'],
      });
    },
  });
}

export function useSetCustomerAddressFreeze(customerId: string) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async ({ addressId, onFreeze }: { addressId: string; onFreeze: boolean }) => {
      const resp = await api.patch<{
        address: {
          id: string;
          onFreeze: boolean;
        };

        orderItemsUpdated: number;
      }>(`/organization/customers/${customerId}/addresses/${addressId}/freeze`, {
        onFreeze,
      });

      return resp.data;
    },

    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: qk.customerAddresses(customerId),
      });

      void qc.invalidateQueries({
        queryKey: ['customer-addresses', customerId],
      });

      void qc.invalidateQueries({
        queryKey: ['addresses', customerId],
      });

      void qc.invalidateQueries({
        queryKey: ['org:orders:items'],
      });

      void qc.invalidateQueries({
        queryKey: ['orders'],
        exact: false,
      });

      void qc.invalidateQueries({
        queryKey: ['auditLogs'],
      });
    },
  });
}

export function useDeleteCustomerAddress(customerId: string) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (addressId: string) => {
      const resp = await api.delete(`/organization/customers/${customerId}/addresses/${addressId}`);

      return resp.data;
    },

    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: qk.customerAddresses(customerId),
      });

      void qc.invalidateQueries({
        queryKey: ['auditLogs'],
      });
    },
  });
}

export function useListCustomerSubusers(customerId: string | null | undefined) {
  return useQuery<AdminCustomerSubuserListResponse>({
    queryKey: ['customer-subusers-admin', customerId],

    queryFn: async () => {
      const { data } = await api.get(`/organization/customers/${customerId}/subusers`);

      return data;
    },

    enabled: !!customerId,
  });
}

export function useInviteCustomerSubuserAdmin(customerId: string | null | undefined) {
  const qc = useQueryClient();

  return useMutation<InviteAdminCustomerSubuserResponse, any, InviteAdminCustomerSubuserPayload>({
    mutationFn: async (payload) => {
      const { data } = await api.post(
        `/organization/customers/${customerId}/subusers/invite`,
        payload
      );

      return data;
    },

    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: ['customer-subusers-admin', customerId],
      });

      void qc.invalidateQueries({
        queryKey: ['auditLogs'],
      });
    },
  });
}

export function useUpdateCustomerSubuserAdmin(customerId: string | null | undefined) {
  const qc = useQueryClient();

  return useMutation<
    any,
    any,
    {
      customerUserId: string;
      data: UpdateAdminCustomerSubuserPayload;
    }
  >({
    mutationFn: async ({ customerUserId, data }) => {
      const res = await api.patch(
        `/organization/customers/${customerId}/subusers/${customerUserId}`,
        data
      );

      return res.data;
    },

    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: ['customer-subusers-admin', customerId],
      });

      void qc.invalidateQueries({
        queryKey: ['auditLogs'],
      });
    },
  });
}

export function useCustomerSubuserAddressAccessAdmin(
  customerId: string | null | undefined,
  customerUserId: string | null | undefined
) {
  return useQuery<AdminCustomerSubuserAddressAccessResponse>({
    queryKey: ['customer-subusers-admin', customerId, customerUserId, 'address-access'],

    queryFn: async () => {
      const { data } = await api.get(
        `/organization/customers/${customerId}/subusers/${customerUserId}/address-access`
      );

      return data;
    },

    enabled: !!customerId && !!customerUserId,
  });
}

export function useUpdateCustomerSubuserAddressAccessAdmin(
  customerId: string | null | undefined,
  customerUserId: string | null | undefined
) {
  const qc = useQueryClient();

  return useMutation<
    any,
    any,
    {
      addresses: Array<{
        deliveryAddressId: string;
        isEnabled: boolean;
      }>;
    }
  >({
    mutationFn: async (payload) => {
      const res = await api.put(
        `/organization/customers/${customerId}/subusers/${customerUserId}/address-access`,
        payload
      );

      return res.data;
    },

    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: ['customer-subusers-admin', customerId],
      });

      void qc.invalidateQueries({
        queryKey: ['customer-subusers-admin', customerId, customerUserId, 'address-access'],
      });

      void qc.invalidateQueries({
        queryKey: ['auditLogs'],
      });
    },
  });
}

export function useAssignCustomerToSalesRep(customerId: string) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (addressId?: string | null) => {
      const res = await api.post(
        `/organization/customers/${customerId}/assign-sales-rep`,
        addressId
          ? {
              addressId,
            }
          : {}
      );

      return res.data;
    },

    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: qk.customerAddresses(customerId),
      });

      void qc.invalidateQueries({
        queryKey: ['org:orders:items'],
      });

      void qc.invalidateQueries({
        queryKey: ['auditLogs'],
      });
    },
  });
}

export function useSetDefaultFulfillmentMethod() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (orgLoadOptionId: string | null) => {
      const res = await api.patch('/customer/default-fulfillment-method', {
        orgLoadOptionId,
      });

      return res.data;
    },

    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: selfCustomerKeys.detail(),
      });
    },
  });
}

export function useEnableCustomerLocationLoadOption(customerId?: string | null) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async ({
      addressId,
      loadOptionId,
    }: {
      addressId: string;
      loadOptionId: string;
    }) => {
      const { data } = await api.post(
        `/organization/load-options/customer-locations/${addressId}/load-options/${loadOptionId}`
      );

      return data;
    },

    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: ['customer-addresses', customerId],
      });

      void qc.invalidateQueries({
        queryKey: ['addresses', customerId],
      });
    },
  });
}

export function useDisableCustomerLocationLoadOption(customerId?: string | null) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async ({
      addressId,
      loadOptionId,
    }: {
      addressId: string;
      loadOptionId: string;
    }) => {
      const { data } = await api.delete(
        `/organization/load-options/customer-locations/${addressId}/load-options/${loadOptionId}`
      );

      return data as {
        ok: boolean;
        alreadyDisabled?: boolean;
        defaultFulfillmentMethodId?: string | null;
      };
    },

    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: ['customer-addresses', customerId],
      });

      void qc.invalidateQueries({
        queryKey: ['addresses', customerId],
      });
    },
  });
}

export type SetCustomerLocationProductAccessInput = {
  addressId: string;
  productId: string;
  isEnabled: boolean;
  isPreview: boolean;
};

export function useSetCustomerLocationProductAccess() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async ({
      addressId,
      productId,
      isEnabled,
      isPreview,
    }: SetCustomerLocationProductAccessInput) => {
      const { data } = await api.put(
        `/organization/customers/customer-locations/${addressId}/products/${productId}`,
        {
          isEnabled,
          isPreview,
        }
      );

      return data;
    },

    onSuccess: (_data, variables) => {
      void qc.invalidateQueries({
        queryKey: ['customer-addresses'],
      });

      void qc.invalidateQueries({
        queryKey: ['products'],
        exact: false,
      });

      void qc.invalidateQueries({
        queryKey: ['customer-location-products', variables.addressId],
        exact: false,
      });

      void qc.invalidateQueries({
        queryKey: ['auditLogs'],
        exact: false,
      });
    },
  });
}

export interface AdminCustomerExportFilters {
  search?: string;
  status?: string;
  hasFrozenLocations?: boolean;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
}

interface AdminCustomerExportPage {
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
  customers: any[];
}

const CUSTOMER_EXPORT_PAGE_SIZE = 300;
const CUSTOMER_EXPORT_BATCH_SIZE = 5;

async function fetchCustomerExportPage(
  filters: AdminCustomerExportFilters,
  page: number
): Promise<AdminCustomerExportPage> {
  const { data } = await api.get<AdminCustomerExportPage>('/organization/customers', {
    params: {
      search: filters.search || undefined,
      status: filters.status || undefined,
      hasFrozenLocations: filters.hasFrozenLocations || undefined,
      sortBy: filters.sortBy || undefined,
      sortDir: filters.sortDir || undefined,
      page,
      pageSize: CUSTOMER_EXPORT_PAGE_SIZE,
    },
  });

  return data;
}

/**
 * Fetch every customer matching the current admin-table filters and sort.
 * The visible table is paginated, so CSV export follows every API page.
 */
export async function fetchAllCustomersForExport(
  filters: AdminCustomerExportFilters
): Promise<any[]> {
  const firstPage = await fetchCustomerExportPage(filters, 1);
  const customers = [...(firstPage.customers ?? [])];
  const totalPages = Math.max(1, Number(firstPage.totalPages) || 1);

  for (let startPage = 2; startPage <= totalPages; startPage += CUSTOMER_EXPORT_BATCH_SIZE) {
    const endPage = Math.min(totalPages, startPage + CUSTOMER_EXPORT_BATCH_SIZE - 1);

    const pages = await Promise.all(
      Array.from({ length: endPage - startPage + 1 }, (_, index) =>
        fetchCustomerExportPage(filters, startPage + index)
      )
    );

    for (const result of pages) {
      customers.push(...(result.customers ?? []));
    }
  }

  return customers;
}
